import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Users, Filter, UserPlus, Bot, Image, BarChart3, Clock, Copy,
  RotateCcw, ChevronRight, TrendingUp, TrendingDown, Calendar,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGES = [
  { id: "today", label: "Today" },
  { id: "7d", label: "7 Days" },
  { id: "30d", label: "30 Days" },
  { id: "all", label: "All Time" },
];

const RANGE_LABELS = { today: "day", "7d": "7 days", "30d": "30 days" };

const STATUS_STYLES = {
  new: { label: "New", cls: "bg-emerald-100 text-emerald-700" },
  contacted: { label: "Contacted", cls: "bg-blue-100 text-blue-700" },
  scheduled: { label: "Estimate Scheduled", cls: "bg-amber-100 text-amber-800" },
  completed: { label: "Completed", cls: "bg-stone-200 text-stone-600" },
};

const AVATAR_COLORS = [
  "bg-amber-100 text-amber-800",
  "bg-rose-100 text-rose-700",
  "bg-indigo-100 text-indigo-700",
  "bg-sky-100 text-sky-700",
  "bg-emerald-100 text-emerald-700",
];

function timeAgo(iso) {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins || 1}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function initials(name) {
  const parts = (name || "?").trim().split(/\s+/);
  return (parts[0]?.[0] || "?") + (parts[1]?.[0] || "");
}

function fmtDuration(s) {
  if (!s) return "0:00";
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function fmtRangeLabel(startIso, endIso) {
  const opts = { month: "short", day: "numeric" };
  const s = new Date(startIso);
  const e = new Date(endIso);
  return `${s.toLocaleDateString("en-US", opts)} – ${e.toLocaleDateString("en-US", opts)}, ${e.getFullYear()}`;
}

function Sparkline({ data, color = "#B8860B" }) {
  if (!data?.length) return null;
  const w = 96;
  const h = 40;
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => `${(i / Math.max(data.length - 1, 1)) * w},${h - (v / max) * (h - 6) - 3}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-24 h-10 shrink-0" aria-hidden="true">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" opacity="0.85" />
    </svg>
  );
}

function Delta({ value, rangeId }) {
  if (value == null || rangeId === "all") return <span className="text-xs text-stone-400"> </span>;
  const up = value >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`flex items-center gap-1.5 text-xs ${up ? "text-emerald-600" : "text-red-500"}`}>
      <Icon className="w-3.5 h-3.5" /> {Math.abs(value)}%
      <span className="text-stone-400">vs. previous {RANGE_LABELS[rangeId]}</span>
    </span>
  );
}

function StatCard({ icon: Icon, label, value, delta, rangeId, spark, testid, onClick }) {
  const inner = (
    <>
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-amber-700">
          <Icon className="w-5 h-5" />
          <span className="text-xs uppercase tracking-wider font-semibold text-stone-500">{label}</span>
        </div>
        <p className="font-display text-3xl sm:text-4xl text-stone-900 mt-2">{value}</p>
        <div className="mt-1.5"><Delta value={delta} rangeId={rangeId} /></div>
        {onClick && <span className="mt-2 inline-block text-xs font-semibold text-amber-700">View sources →</span>}
      </div>
      <div className="self-center"><Sparkline data={spark} /></div>
    </>
  );
  if (onClick) {
    return (
      <button onClick={onClick} className="gold-ring-card rounded-xl p-5 sm:p-6 flex justify-between gap-4 text-left w-full" data-testid={testid}>
        {inner}
      </button>
    );
  }
  return (
    <div className="gold-ring-card rounded-xl p-5 sm:p-6 flex justify-between gap-4" data-testid={testid}>
      {inner}
    </div>
  );
}

function LineChart({ series }) {
  if (!series?.length) return null;
  const W = 600;
  const H = 210;
  const padL = 34;
  const padB = 24;
  const padT = 10;
  const maxY = Math.max(5, ...series.map((d) => Math.max(d.visitors, d.leads, d.previews)));
  const x = (i) => padL + (i / Math.max(series.length - 1, 1)) * (W - padL - 8);
  const y = (v) => padT + (1 - v / maxY) * (H - padT - padB);
  const lines = [
    { key: "visitors", color: "#B8860B", label: "Visitors" },
    { key: "leads", color: "#D9B96A", label: "Leads" },
    { key: "previews", color: "#6B4423", label: "AI Previews" },
  ];
  const labelEvery = Math.max(1, Math.ceil(series.length / 6));
  const gridSteps = 4;

  return (
    <div data-testid="performance-chart">
      <div className="flex flex-wrap items-center gap-5 mb-4">
        {lines.map((l) => (
          <span key={l.key} className="flex items-center gap-2 text-xs text-stone-600 font-medium">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: l.color }} /> {l.label}
          </span>
        ))}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
        {Array.from({ length: gridSteps + 1 }, (_, i) => {
          const gy = padT + (i / gridSteps) * (H - padT - padB);
          const val = Math.round(maxY * (1 - i / gridSteps));
          return (
            <g key={i}>
              <line x1={padL} y1={gy} x2={W - 8} y2={gy} stroke="#e7e0d4" strokeWidth="1" />
              <text x={padL - 8} y={gy + 4} textAnchor="end" fontSize="10" fill="#a8a29e">{val}</text>
            </g>
          );
        })}
        {series.map((d, i) =>
          i % labelEvery === 0 || i === series.length - 1 ? (
            <text key={d.date} x={x(i)} y={H - 6} textAnchor="middle" fontSize="10" fill="#a8a29e">
              {new Date(`${d.date}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            </text>
          ) : null
        )}
        {lines.map((l) => (
          <g key={l.key}>
            <polyline
              points={series.map((d, i) => `${x(i)},${y(d[l.key])}`).join(" ")}
              fill="none" stroke={l.color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"
            />
            {series.map((d, i) => (
              <circle key={i} cx={x(i)} cy={y(d[l.key])} r="3" fill={l.color} />
            ))}
          </g>
        ))}
      </svg>
    </div>
  );
}

const authFetch = (url, options = {}) => fetch(url, { credentials: "include", ...options });

export default function AdminStats({ onViewAll, onShowSources }) {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const res = await authFetch(`${API}/admin/analytics?range=${range}`);
    if (res.ok) setData(await res.json());
  }, [range]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [load]);

  const resetStats = async () => {
    if (!window.confirm("Reset all visit and AI preview stats to zero? Leads are not affected. This can't be undone.")) return;
    const res = await authFetch(`${API}/admin/analytics/reset`, { method: "POST" });
    if (res.ok) {
      toast.success("Stats reset to zero.");
      load();
    }
  };

  const spark = (key) => data?.series?.map((d) => d[key]) || [];

  return (
    <div data-testid="admin-stats-tab">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {RANGES.map((r) => (
          <button
            key={r.id}
            onClick={() => setRange(r.id)}
            data-testid={`stats-range-${r.id}`}
            className={`text-xs uppercase tracking-wider font-semibold px-4 py-2 rounded-full border transition-colors duration-300 ${
              range === r.id ? "bg-amber-600 text-white border-amber-600 shadow-md" : "border-amber-600/25 bg-white text-stone-500 hover:text-amber-800"
            }`}
          >
            {r.label}
          </button>
        ))}
        {data && (
          <span className="ml-auto hidden sm:flex items-center gap-2 text-xs text-stone-500 bg-white border border-amber-600/20 rounded-full px-4 py-2" data-testid="stats-range-label">
            <Calendar className="w-3.5 h-3.5 text-amber-700" />
            {range === "today" ? "Today" : range === "all" ? "All Time" : fmtRangeLabel(data.range_start, data.range_end)}
          </span>
        )}
        <button
          onClick={resetStats}
          data-testid="stats-reset-btn"
          className="ml-auto sm:ml-0 text-xs uppercase tracking-wider font-semibold text-red-600 hover:text-red-700 border border-red-600/30 hover:border-red-600/60 px-4 py-2 rounded-full transition-colors duration-300 flex items-center gap-1.5"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Reset Stats
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <StatCard icon={Users} label="Visitors" value={data?.visitors ?? "—"} delta={data?.prev?.visitors} rangeId={range} spark={spark("visitors")} testid="stat-visitors" onClick={onShowSources} />
        <StatCard icon={Filter} label="Total Leads" value={data?.leads ?? "—"} delta={data?.prev?.leads} rangeId={range} spark={spark("leads")} testid="stat-total-leads" />
        <StatCard icon={UserPlus} label="New Leads" value={data?.new_leads ?? "—"} delta={data?.prev?.new_leads} rangeId={range} spark={spark("leads")} testid="stat-new-leads" />
        <StatCard icon={Bot} label="AI Leads" value={data?.ai_leads ?? "—"} delta={data?.prev?.ai_leads} rangeId={range} spark={spark("leads")} testid="stat-ai-leads" />
        <StatCard icon={Image} label="AI Previews" value={data?.previews ?? "—"} delta={data?.prev?.previews} rangeId={range} spark={spark("previews")} testid="stat-ai-previews" />
        <StatCard icon={BarChart3} label="Lead Rate" value={data ? `${data.lead_rate}%` : "—"} delta={data?.prev?.lead_rate} rangeId={range} spark={spark("leads")} testid="stat-lead-rate" />
        <StatCard icon={Clock} label="Avg. Time on Site" value={data ? fmtDuration(data.avg_seconds) : "—"} delta={data?.prev?.avg_seconds} rangeId={range} spark={spark("visitors")} testid="stat-avg-time" />
        <StatCard icon={Copy} label="Avg. Previews / AI Lead" value={data ? data.avg_previews_per_ai_lead.toFixed(1) : "—"} delta={data?.prev?.avg_previews_per_ai_lead} rangeId={range} spark={spark("previews")} testid="stat-previews-per-lead" />
      </div>

      <div className="gold-ring-card rounded-xl p-5 sm:p-7 mt-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl sm:text-2xl text-stone-900">Website Performance</h2>
          <span className="text-xs text-stone-500">{RANGES.find((r) => r.id === range)?.label === "All Time" ? "All Time" : `Last ${RANGE_LABELS[range] || "period"}`}</span>
        </div>
        <LineChart series={data?.series} />
      </div>

      <div className="gold-ring-card rounded-xl p-5 sm:p-7 mt-6" data-testid="ai-lead-activity">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display text-xl sm:text-2xl text-stone-900">AI Lead Activity</h2>
          <button onClick={onViewAll} data-testid="ai-activity-view-all" className="text-xs font-semibold text-stone-500 hover:text-amber-700 flex items-center gap-1 transition-colors duration-200">
            View All <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
        {data?.ai_activity?.length ? (
          <div className="divide-y divide-amber-600/10">
            {data.ai_activity.map((lead, i) => {
              const style = STATUS_STYLES[lead.status] || STATUS_STYLES.new;
              return (
                <button
                  key={lead.id}
                  onClick={onViewAll}
                  data-testid={`ai-activity-${lead.id}`}
                  className="w-full py-4 flex items-center gap-4 text-left hover:bg-amber-50/50 rounded-lg px-2 -mx-2 transition-colors duration-200"
                >
                  <span className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${AVATAR_COLORS[i % AVATAR_COLORS.length]}`}>
                    {initials(lead.name)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-stone-900 font-semibold truncate">{lead.name}</span>
                    <span className="block text-xs text-stone-500 mt-0.5">
                      {lead.previews} preview{lead.previews === 1 ? "" : "s"} · {timeAgo(lead.created_at)}
                    </span>
                  </span>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full shrink-0 ${style.cls}`}>{style.label}</span>
                  <ChevronRight className="w-4 h-4 text-stone-400 shrink-0" />
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-stone-500 py-8 text-center">No AI preview leads yet. They'll appear here when visitors use the floor visualizer.</p>
        )}
      </div>
    </div>
  );
}
